function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { compact, isEqual } from 'lodash-es';
import ServicePanel from "../ServicePanel/ServicePanel";
import { invoiceDraftModel, serviceBillingModel, serviceModel } from "../../models";
import { Callout, DropdownMenu, DropdownMenuItem, IconButton, Spinner } from 'seagull';
import { Edit2Liner24, MoreVertical, Void } from 'seagull/icons';
import { useServiceAdvancedSettingsAdditionalSection } from "../ServiceAdvancedSettings";
import { changeServicePriceAndDiscount, disableEditBillingInitiation, getAmendedNextBillingDateText, getServiceAutoApprovalDate, shouldShowDiscountExpirationWarning, showOnApprovalInfo } from "./EditAgreementModal.utils";
import { BillingDayOfMonthModel } from "../../models";
import { getServiceChangeBullets } from "../../business-logic/amendmentsLogic";
import AmendmentChanges from "../AmendmentChanges/AmendmentChanges";
import { useOverlayContext, useIoContext, useStoreActions } from "../../contexts";
import { handleRequest } from "../../infra/utils";
import ServiceBuilder from "../ServiceCreation/ServiceBuilder/ServiceBuilder";
import TerminateServiceModal from "../TerminateServiceModal";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { fromDateStampObject } from "../../models/time";
import EditServiceContentModal from "../EditServiceContentModal/EditServiceContentModal";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const disabledOneTimeInStagesTooltip = "Services set to 'Bill in stages' can't be modified. To make changes, remove this service and add a new one to the agreement.";
const EditAgreementService = /*#__PURE__*/forwardRef(function EditAgreementService(props, ref) {
  const {
    service,
    relationship,
    agreementData,
    idx,
    showServiceValidationWarning,
    originalService,
    onServiceChange,
    onRemoveService
  } = props;
  const testPrefix = "service-".concat(idx);
  const {
    getNow
  } = useIoContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const featureFlags = useFeatureFlags();
  const {
    SERVICE_TEMPLATE_BUNDLE = false
  } = featureFlags;
  const [amendedNextBillingDate, setAmendedNextBillingDate] = useState();
  const [loadingAmendedNextBillingDate, setLoadingAmendedNextBillingDate] = useState(false);
  const isUnsaved = !isEqual(originalService, service);
  const isFreshlyAdded = !originalService;
  const disableStatusBadge = serviceModel.isAmended(service.status) && !!originalService && !serviceModel.isAmended(originalService.status);
  const isExistingOneTimeInStages = serviceBillingModel.isOneTimeInStages(service.billing) && !isFreshlyAdded;
  const shouldShowDiscountExpirationCallout = shouldShowDiscountExpirationWarning(service, originalService);
  const changeBullets = useMemo(() => getServiceChangeBullets(service, SERVICE_TEMPLATE_BUNDLE), [service, SERVICE_TEMPLATE_BUNDLE]);
  const autoApprovalDate = useMemo(() => getServiceAutoApprovalDate(service, relationship, getNow()), [service, relationship]);
  const onStartBillingAtChange = useCallback((serviceId, startBillingAt) => {
    // todo: next step - implement for amended services
    if (!serviceModel.isAwaitingApproval(service.status)) return;
    onServiceChange(serviceModel.setStartBillingAt(service, startBillingAt));
  }, [service, onServiceChange]);
  const serviceAdvancedSettingsAdditionalSection = useServiceAdvancedSettingsAdditionalSection({
    relationshipState: {
      type: 'approved',
      effectiveDate: relationship.effectiveDate ? fromDateStampObject(relationship.effectiveDate) : null
    },
    service: serviceModel.applyChanges(service),
    testId: testPrefix,
    showServiceValidationWarning,
    isReadOnly: false,
    onPausedChange,
    onBillOnAcceptanceChange,
    onProratedChange,
    onLimitBillingTimesChange,
    onAutoPriceIncreaseChange,
    onQboClassChange,
    onDiscountPeriodExpirationChange,
    onPreapprovedAmountChange,
    onStartBillingAtChange,
    isFreshlyAdded
  });
  async function onEditService() {
    logBI(11005, {
      name: service.name,
      id: service.id
    });
    const editedService = await overlayContext.showModal({
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(ServiceBuilder, _objectSpread({
        applyOutputToChanges: !serviceModel.isAwaitingApproval(service.status),
        service: serviceModel.applyChanges(service),
        serviceOrigin: "agreement",
        isFreshlyAdded: isFreshlyAdded,
        agreementData: agreementData
      }, p))
    });
    if (!editedService) return;
    if (serviceModel.isAwaitingApproval(service.status)) {
      handleAwaitingApprovalService(editedService);
    } else {
      handleApprovedService(editedService);
    }
  }
  async function onEditServiceNameAndDescription() {
    await overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(EditServiceContentModal, {
          width: "900px",
          title: "Service name & description",
          providedServiceName: service.name,
          providedServiceDescription: service.description,
          dismissModal: dismissModal,
          onSave: _ref2 => {
            let {
              name,
              description
            } = _ref2;
            onServiceChange(_objectSpread(_objectSpread({}, service), {}, {
              name,
              description
            }));
            dismissModal();
          }
        });
      }
    });
  }
  function handleAwaitingApprovalService(editedService) {
    logBI(11011, {
      id: editedService.id,
      name: editedService.name
    });
    onServiceChange(editedService);
  }
  function handleApprovedService(editedService) {
    const updatedService = handleDiscountExpiration(editedService);
    const withChanges = serviceModel.applyChanges(updatedService);
    logBI(11011, {
      id: withChanges.id,
      name: withChanges.name
    });
    const newChanges = serviceModel.changes(updatedService);
    if (newChanges) onServiceChange(serviceModel.setChanges(service, newChanges));
  }
  function handleDiscountExpiration(editedService) {
    var _originalService$cost;
    if (!serviceModel.isAmended(editedService.status)) {
      return editedService;
    }
    const shouldShowWarning = shouldShowDiscountExpirationWarning(editedService, originalService);
    const originalExpiration = originalService === null || originalService === void 0 || (_originalService$cost = originalService.cost.discount) === null || _originalService$cost === void 0 ? void 0 : _originalService$cost.expiration();
    if (shouldShowWarning) {
      var _editedService$status, _editedService$status2;
      // removing the expiration when rate/discount changed
      return serviceModel.setDiscountChanges(editedService, (_editedService$status = (_editedService$status2 = editedService.status.amended.changes.cost) === null || _editedService$status2 === void 0 || (_editedService$status2 = _editedService$status2.discount) === null || _editedService$status2 === void 0 ? void 0 : _editedService$status2.setExpiration(undefined)) !== null && _editedService$status !== void 0 ? _editedService$status : null);
    } else if (originalExpiration) {
      var _editedService$status3, _editedService$status4;
      // preserve original expiration
      return serviceModel.setDiscountChanges(editedService, (_editedService$status3 = (_editedService$status4 = editedService.status.amended.changes.cost) === null || _editedService$status4 === void 0 || (_editedService$status4 = _editedService$status4.discount) === null || _editedService$status4 === void 0 ? void 0 : _editedService$status4.setExpiration(originalExpiration)) !== null && _editedService$status3 !== void 0 ? _editedService$status3 : null);
    }
    return editedService;
  }
  function onStopService() {
    overlayContext.showModal({
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/_jsx(TerminateServiceModal, {
          serviceName: service.name,
          agreementId: relationship.id,
          stoppageMayAffectNextBill: serviceBillingModel.isOneTimeManual(service.billing) || serviceBillingModel.isRepeatableManual(service.billing),
          canPauseService: serviceBillingModel.isOngoing(service.billing),
          onKeepService: () => {
            dismissModal();
            onServiceChange(serviceModel.setTerminated(service));
          },
          onStopService: dismissModal
        });
      }
    });
  }
  function onProratedChange(serviceId, prorated) {
    onServiceChange(serviceModel.setProrated(service, prorated));
  }
  function onNoteChange(note) {
    onServiceChange(serviceModel.setNote(service, note));
  }
  function onPausedChange(serviceId, paused) {
    onServiceChange(serviceModel.setPaused(service, paused));
  }
  function onBillOnAcceptanceChange(serviceId, billOnAcceptance) {
    onServiceChange(serviceModel.setSkipBillingOnAcceptance(service, !billOnAcceptance, featureFlags));
  }
  function onLimitBillingTimesChange(serviceId, enabled, limit) {
    const newService = enabled ? serviceModel.setMaxCharges(service, enabled, limit) : serviceModel.deleteMaxCharges(service);
    onServiceChange(newService);
  }
  function onAutoPriceIncreaseChange(serviceId, priceIncrease) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setPriceIncrease(service, priceIncrease) : serviceModel.setPriceIncreaseChanges(service, priceIncrease);
    onServiceChange(newService);
  }
  function onBillUpfrontModeChange(serviceId, billUpfront) {
    onServiceChange(serviceModel.setBillUpfront(service, billUpfront));
  }
  function onIncludedServicesChange(includedServices) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setIncludedServices(service, includedServices) : serviceModel.setIncludedServicesChanges(service, includedServices);
    onServiceChange(newService);
  }
  function onDayOfMonthChange(serviceId, dayOfMonth) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setBillingDayOfMonth(service, dayOfMonth) : serviceModel.setBillingDayOfMonthChanges(service, dayOfMonth);
    onServiceChange(newService);
    if (originalService && !BillingDayOfMonthModel.isEqual(serviceModel.billingDayOfMonth(serviceModel.applyChanges(originalService)), serviceModel.billingDayOfMonth(serviceModel.applyChanges(newService)))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onDayOfWeekChange(serviceId, dayOfWeek) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setBillingDayOfWeek(service, dayOfWeek) : serviceModel.setBillingDayOfWeekChanges(service, dayOfWeek);
    onServiceChange(newService);
    if (originalService && serviceModel.billingDayOfWeek(serviceModel.applyChanges(originalService)) !== serviceModel.billingDayOfWeek(serviceModel.applyChanges(newService))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onDayOfYearChange(serviceId, dayOfYear) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setBillingDayOfYear(service, dayOfYear) : serviceModel.setBillingDayOfYearChanges(service, dayOfYear);
    onServiceChange(newService);
    if (originalService && serviceModel.billingDayOfYear(serviceModel.applyChanges(originalService)) !== serviceModel.billingDayOfYear(serviceModel.applyChanges(newService))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onQboClassChange(serviceId, integratedClass) {
    const newService = serviceModel.setIntegratedClass(service, integratedClass);
    onServiceChange(newService);
  }
  function onDiscountPeriodExpirationChange(serviceId, discountPeriodExpiration) {
    onServiceChange(serviceModel.setDiscountPeriodExpiration(service, discountPeriodExpiration));
  }
  function onPreapprovedAmountChange(serviceId, amount, controlledByAdvancedSettings) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setPreapprovedAmount(service, amount, controlledByAdvancedSettings) : serviceModel.setPreapprovedAmountChanges(service, amount, controlledByAdvancedSettings);
    onServiceChange(newService);
  }
  function onDiscardChanges() {
    if (serviceModel.isAwaitingApproval(service.status)) {
      if (originalService) {
        logBI(11007, {
          id: service.id,
          name: service.name
        });
        onServiceChange(serviceModel.setCanceled(service));
      } else {
        onRemoveService(service.id);
      }
    } else {
      logBI(11012, {
        id: service.id,
        name: service.name
      });
      onServiceChange(serviceModel.discardChanges(service));
    }
    clearAmendedNextBillingDate();
  }
  async function fetchAndSetAmendedNextBillingDate(service) {
    setLoadingAmendedNextBillingDate(true);
    const result = await handleRequest(api.invoiceDraft.getForAmendedService(relationship.id, service));
    if (!result.success) {
      clearAmendedNextBillingDate();
    } else {
      const {
        issueDate,
        lineItem
      } = result.data;
      const isOverlappingPeriod = invoiceDraftModel.isOverlappingPeriod(lineItem);
      if (isOverlappingPeriod) {
        setAmendedNextBillingDate(issueDate);
      } else {
        clearAmendedNextBillingDate();
      }
    }
    setLoadingAmendedNextBillingDate(false);
  }
  function clearAmendedNextBillingDate() {
    if (amendedNextBillingDate) setAmendedNextBillingDate(null);
  }
  return /*#__PURE__*/_jsx(ServicePanel, {
    onEditServiceNameAndDescription: onEditServiceNameAndDescription,
    context: "agreement",
    showBadges: true,
    isVendor: true,
    billingInitiationProps: {
      isExistingService: !!originalService
    },
    service: service,
    idx: idx,
    ref: ref,
    error: showServiceValidationWarning,
    disableStatusBadge: disableStatusBadge,
    agreementData: agreementData,
    disableEditBillUpfront: !isFreshlyAdded,
    isFreshlyAdded: isFreshlyAdded,
    disableEditBillingDay: !isFreshlyAdded && serviceBillingModel.isRepeatableManual(service.billing),
    disableEditCost: isExistingOneTimeInStages ? {
      disabledTooltip: disabledOneTimeInStagesTooltip
    } : false,
    disableEditBillingInitiation: disableEditBillingInitiation({
      originalService
    }),
    onDayOfMonthChange: onDayOfMonthChange,
    onDayOfWeekChange: onDayOfWeekChange,
    onDayOfYearChange: onDayOfYearChange,
    onBillUpfrontModeChange: onBillUpfrontModeChange,
    onCostChange: cost => onServiceChange(changeServicePriceAndDiscount(service, cost)),
    onChangeService: onServiceChange,
    onIncludedServicesChange: onIncludedServicesChange,
    additionalSections: compact([showOnApprovalInfo(service, originalService) && {
      disableBorder: true,
      value: /*#__PURE__*/_jsx(Callout, {
        "data-testid": "".concat(testPrefix, "-onApproval-service-info"),
        children: "Since this is a one-time, automatically billed service, an invoice will be issued once amendments are saved and payment will require client approval."
      })
    }, shouldShowDiscountExpirationCallout && {
      disableBorder: true,
      value: /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": "".concat(testPrefix, "-callout-discount-expiration"),
        children: "Changing the service's rate or discount will delete the discount expiration settings"
      })
    }, amendedNextBillingDate && {
      disableBorder: true,
      value: /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": "".concat(testPrefix, "-amended-next-billing-date"),
        children: loadingAmendedNextBillingDate ? /*#__PURE__*/_jsx(Spinner, {
          centeredInBox: true,
          centerBoxHeight: 30,
          size: 30,
          "data-testid": "".concat(testPrefix, "-amended-next-billing-date-loading")
        }) : getAmendedNextBillingDateText(service, amendedNextBillingDate)
      })
    }, serviceAdvancedSettingsAdditionalSection]),
    footer: changeBullets.length > 0 && /*#__PURE__*/_jsx(AmendmentChanges, {
      testid: "".concat(testPrefix, "-changes"),
      items: changeBullets,
      existingNote: serviceModel.note(service),
      isUnsaved: isUnsaved,
      autoApprovalDate: autoApprovalDate,
      onDiscard: onDiscardChanges,
      onNoteChange: onNoteChange
    }),
    menu: !(serviceModel.isCompleted(service.status) || serviceModel.isTerminated(service.status)) && /*#__PURE__*/_jsxs(DropdownMenu, {
      target: /*#__PURE__*/_jsx(IconButton, {
        type: "button",
        "data-testid": "".concat(testPrefix, "-menu"),
        children: /*#__PURE__*/_jsx(MoreVertical, {})
      }),
      children: [/*#__PURE__*/_jsx(DropdownMenuItem, {
        iconStart: /*#__PURE__*/_jsx(Edit2Liner24, {}),
        "data-testid": "".concat(testPrefix, "-edit"),
        disabled: isExistingOneTimeInStages,
        tooltipProps: isExistingOneTimeInStages ? {
          title: disabledOneTimeInStagesTooltip
        } : undefined,
        onClick: onEditService,
        children: "Edit"
      }), (serviceModel.isAmended(service.status) || serviceModel.isApproved(service.status)) && /*#__PURE__*/_jsx(DropdownMenuItem, {
        iconStart: /*#__PURE__*/_jsx(Void, {}),
        "data-testid": "".concat(testPrefix, "-stop"),
        onClick: onStopService,
        children: "Stop"
      })]
    })
  });
});
export default EditAgreementService;